import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { NewSection } from '../NewSection';
import { CardsLayout } from '../ui/CardsLayout';
import { ItemCard } from '../ui/ItemCard';
import { SkeletonLoaders } from './SkeletonLoaders';
import { useBookmarks } from '../../hooks/useBookmarks';
import { SEARCH_RESULT_ROUTES } from '../../utils/helpers/constants';
import { searchVippedResidencesItemsQuery } from '../../api/queries';
import { SearchVippedResidencesItemsQuery } from '../../graphql/graphql';
import { ItemScopeEnum, ItemSort } from '../../graphql/types';

export interface VippedResidencesItemsSectionRef {
  refetch: () => Promise<ApolloQueryResult<SearchVippedResidencesItemsQuery>>;
}

interface VippedResidencesItemsSectionProps {
  filter: { [key: string]: boolean | string };
  onError: () => void;
}

export const VippedResidencesItemsSection = forwardRef<
  VippedResidencesItemsSectionRef,
  VippedResidencesItemsSectionProps
>(({ filter, onError }, ref) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isBookmarked } = useBookmarks();

  const isItemsAllPage = router.pathname === SEARCH_RESULT_ROUTES.ITEMS_ALL;
  const searchVippedResidencesQueryVariables = {
    filter: { ...filter, scope: ItemScopeEnum.VippedApartments },
    limit: 4,
    sort: ItemSort.Random
  };

  const {
    loading: searchVippedResidencesItemsQueryLoading,
    data: searchVippedResidencesItemsQueryData,
    error: searchVippedResidencesItemsQueryError,
    refetch: searchVippedResidencesItemsQueryRefetch
  } = useQuery<SearchVippedResidencesItemsQuery>(
    searchVippedResidencesItemsQuery,
    {
      variables: searchVippedResidencesQueryVariables,
      ssr: false,
      notifyOnNetworkStatusChange: true,
      skip: isItemsAllPage
    }
  );

  useImperativeHandle(ref, () => ({
    refetch: searchVippedResidencesItemsQueryRefetch
  }));

  useEffect(() => {
    if (searchVippedResidencesItemsQueryError) {
      onError();
    }
  }, [searchVippedResidencesItemsQueryError]);

  const itemsVipResidencesList =
    (searchVippedResidencesItemsQueryData?.items &&
      searchVippedResidencesItemsQueryData.items.map(item => (
        <ItemCard
          key={item.id}
          {...item}
          inBookmarks={isBookmarked(item.id)}
          stat="item_card_search_results_vip"
        />
      ))) ||
    [];

  if (searchVippedResidencesItemsQueryLoading) {
    return <SkeletonLoaders />;
  }

  if (!itemsVipResidencesList.length) {
    return null;
  }

  return (
    <NewSection
      id="search-page-vipped-residences"
      title={t('sections.vipped_residences.title')}
      headlineType="h3"
    >
      <CardsLayout>{itemsVipResidencesList}</CardsLayout>
    </NewSection>
  );
});

VippedResidencesItemsSection.displayName = 'VippedResidencesItemsSection';
